<template>
  <div class="contrato-evolucion">
    <div class="card bg-white" style="border-radius: 1rem">
      <div class="card-body">
        <div class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
          <div>
            <h4 class="mb-1 d-flex align-items-center gap-2">
              <i class="fas fa-chart-line text-primary"></i>
              <span>Evolución de contratos</span>
            </h4>
            <p class="text-muted mb-0 small">
              Barrido de leads con <code>numeroContrato</code> contra la API Saludsa.
              Snapshots automáticos lunes y jueves 08:00 (Guayaquil).
            </p>
          </div>
          <button
            class="btn btn-primary btn-sm"
            :disabled="runLoading || loadLoading"
            @click="runSnapshot"
          >
            <span
              v-if="runLoading"
              class="spinner-border spinner-border-sm me-1"
              role="status"
            ></span>
            {{ runLoading ? "Generando..." : "Generar snapshot ahora" }}
          </button>
        </div>

        <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

        <div v-if="loadLoading" class="text-center py-4 text-muted">
          <i class="fas fa-spinner fa-spin fa-2x"></i>
          <p class="mt-2 mb-0">Cargando historial...</p>
        </div>

        <template v-else>
          <div v-if="latest" class="row g-2 mb-3">
            <div class="col-6 col-md-3">
              <div class="border rounded p-2 text-center">
                <div class="small text-muted">Último snapshot</div>
                <div class="fw-semibold small">{{ formatDate(latest.snapshotAt) }}</div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="border rounded p-2 text-center">
                <div class="small text-muted">Contratos únicos</div>
                <div class="fw-semibold">{{ lastSnapshotMeta.uniqueContratos ?? "—" }}</div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="border rounded p-2 text-center">
                <div class="small text-muted">Consultas OK</div>
                <div class="fw-semibold text-success">{{ lastSnapshotMeta.queriedOk ?? "—" }}</div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="border rounded p-2 text-center">
                <div class="small text-muted">Sin datos válidos</div>
                <div class="fw-semibold text-danger">{{ lastSnapshotMeta.queriedError ?? "—" }}</div>
              </div>
            </div>
          </div>

          <div v-if="!snapshots.length" class="text-center py-4 text-muted">
            <i class="far fa-chart-bar fa-2x mb-2"></i>
            <p class="mb-0">No hay snapshots aún. Genera el primero con el botón de arriba.</p>
          </div>

          <div v-else class="row g-3">
            <div class="col-12 col-lg-7">
              <div class="card border-0 bg-light p-3" style="min-height: 320px">
                <h6 class="mb-2">Tendencia por estado (incl. API Error)</h6>
                <div style="height: 280px">
                  <LineChart
                    v-if="lineChartData"
                    :chart-data="lineChartData"
                    :styles="{ height: '280px' }"
                  />
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-5">
              <div class="card border-0 bg-light p-3" style="min-height: 320px">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0">Distribución por estado</h6>
                  <select
                    v-if="snapshots.length > 1"
                    v-model="selectedSnapshotId"
                    class="form-select form-select-sm"
                    style="max-width: 180px"
                  >
                    <option
                      v-for="s in snapshotsForSelect"
                      :key="s.id"
                      :value="s.id"
                    >
                      {{ formatDate(s.snapshotAt) }}
                    </option>
                  </select>
                </div>
                <div style="height: 280px">
                  <PieChart
                    v-if="pieChartData"
                    :chart-data="pieChartData"
                    :styles="{ height: '280px' }"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="snapshots.length && selectedSnapshot"
            class="mt-3 pt-2 border-top"
          >
            <button
              type="button"
              class="btn btn-link btn-sm p-0 text-start text-primary debug-toggle"
              @click="debugOpen = !debugOpen"
            >
              <i
                class="fas me-1"
                :class="debugOpen ? 'fa-chevron-down' : 'fa-chevron-right'"
              ></i>
              {{ debugOpen ? "Ocultar" : "Ver" }} detalle de depuración
              <span class="text-muted fw-normal">
                (estados OK, errores API y muestra de contratos)
              </span>
            </button>

            <div v-show="debugOpen" class="row g-3 mt-2">
              <div class="col-12 col-md-6" v-if="okStateRows.length">
                <div class="card border-0 bg-light p-3 h-100">
                  <h6 class="mb-2 text-success">OK con datos — Estado contrato</h6>
                  <table class="table table-sm table-bordered mb-0 bg-white">
                    <thead>
                      <tr>
                        <th>Estado</th>
                        <th class="text-end">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in okStateRows" :key="row.label">
                        <td>{{ row.label }}</td>
                        <td class="text-end fw-semibold">{{ row.count }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="col-12 col-md-6" v-if="remesaRows.length">
                <div class="card border-0 bg-light p-3 h-100">
                  <h6 class="mb-2 text-success">OK con datos — Estado remesa</h6>
                  <table class="table table-sm table-bordered mb-0 bg-white">
                    <thead>
                      <tr>
                        <th>Remesa</th>
                        <th class="text-end">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in remesaRows" :key="row.label">
                        <td>{{ row.label }}</td>
                        <td class="text-end fw-semibold">{{ row.count }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="col-12" v-if="errorRows.length">
                <div class="card border-0 bg-light p-3">
                  <h6 class="mb-2 text-danger">Consultas sin datos válidos</h6>
                  <table class="table table-sm table-bordered mb-0 bg-white">
                    <thead>
                      <tr>
                        <th>Motivo</th>
                        <th class="text-end">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in errorRows" :key="row.label">
                        <td><code class="small">{{ row.label }}</code></td>
                        <td class="text-end fw-semibold">{{ row.count }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-if="errorDetailCount" class="mt-3">
                    <p class="small text-muted mb-1">
                      <template v-if="errorSamplesIncomplete">
                        Mostrando {{ errorSamples.length }} de {{ errorDetailCount }} contratos con error.
                        <span class="text-warning">
                          Este snapshot se guardó con un límite de 50; genera uno nuevo para ver el detalle completo.
                        </span>
                      </template>
                      <template v-else>
                        Mostrando {{ errorDetailCount }} contrato{{ errorDetailCount === 1 ? '' : 's' }} con error
                      </template>
                    </p>
                    <div class="table-responsive" style="max-height: 280px">
                      <table class="table table-sm table-striped mb-0 small">
                        <thead>
                          <tr>
                            <th>Nº contrato</th>
                            <th>Motivo</th>
                            <th>Estado Saludsa</th>
                            <th>Mensaje</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(s, idx) in errorSamples" :key="idx">
                            <td>{{ s.numeroContrato }}</td>
                            <td><code>{{ s.reason }}</code></td>
                            <td>{{ s.saludsaEstado || "—" }}</td>
                            <td class="text-muted">{{ s.mensaje || "—" }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="!okStateRows.length && !remesaRows.length && !errorRows.length"
                class="col-12 text-muted small"
              >
                Sin filas de detalle para este snapshot.
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import LineChart from "@/components/LineChart.vue";
import PieChart from "@/components/PieChart.vue";
import moment from "moment";

const API_ERROR_LABEL = "API Error";
const COLOR_API_ERROR = "#dc3545";

const LINE_COLORS = {
  Activo: "#28a745",
  Pendiente: "#ffc107",
  Sin_estado: "#6c757d",
  Anulado: "#dc3545",
  Cancelado: "#dc3545",
  [API_ERROR_LABEL]: COLOR_API_ERROR,
  Error_API: COLOR_API_ERROR,
};

function chartColor(label) {
  if (label === API_ERROR_LABEL || label === "Error_API") return COLOR_API_ERROR;
  return LINE_COLORS[label] || "#114d8c";
}

function apiErrorCount(snapshot) {
  if (!snapshot) return 0;
  if (snapshot.queriedError != null && snapshot.queriedError > 0) {
    return snapshot.queriedError;
  }
  const err = snapshot.byConsultaError || {};
  return Object.values(err).reduce((sum, n) => sum + (Number(n) || 0), 0);
}

function mapToRows(obj) {
  if (!obj) return [];
  return Object.entries(obj)
    .filter(([, v]) => v > 0)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

export default {
  name: "ContratoEvolucionReport",
  components: { LineChart, PieChart },
  props: {
    tokenAuth: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      role: localStorage.getItem("session"),
      loadLoading: false,
      runLoading: false,
      error: "",
      snapshots: [],
      estados: [],
      latest: null,
      selectedSnapshotId: null,
      lastSnapshotMeta: {},
      debugOpen: false,
    };
  },
  computed: {
    snapshotsForSelect() {
      return [...this.snapshots].sort(
        (a, b) => new Date(b.snapshotAt) - new Date(a.snapshotAt),
      );
    },
    selectedSnapshot() {
      if (!this.snapshots.length) return null;
      const id = this.selectedSnapshotId || this.latest?.id;
      return this.snapshots.find((s) => s.id === id) || this.snapshots[this.snapshots.length - 1];
    },
    okStateRows() {
      return mapToRows(this.selectedSnapshot?.byEstadoContrato);
    },
    remesaRows() {
      return mapToRows(this.selectedSnapshot?.byEstadoRemesa);
    },
    errorRows() {
      return mapToRows(this.selectedSnapshot?.byConsultaError);
    },
    errorSamples() {
      return this.selectedSnapshot?.errorSamples || [];
    },
    errorDetailCount() {
      return this.selectedSnapshot?.queriedError ?? this.errorSamples.length;
    },
    errorSamplesIncomplete() {
      const total = this.selectedSnapshot?.queriedError;
      if (total == null) return false;
      return this.errorSamples.length < total;
    },
    chartSeriesKeys() {
      const keys = (this.estados || []).filter(
        (k) => k !== "Error_API" && k !== API_ERROR_LABEL,
      );
      const sorted = [...keys].sort((a, b) => {
        if (a === "Activo") return -1;
        if (b === "Activo") return 1;
        return a.localeCompare(b);
      });
      const hasErrors = this.snapshots.some((s) => apiErrorCount(s) > 0);
      if (hasErrors) sorted.push(API_ERROR_LABEL);
      return sorted;
    },
    lineChartData() {
      if (!this.snapshots.length || !this.chartSeriesKeys.length) return null;
      const labels = this.snapshots.map((s) =>
        moment(s.snapshotAt).format("DD/MM/YY"),
      );
      const datasets = this.chartSeriesKeys.map((key) => ({
        label: key,
        data: this.snapshots.map((s) => {
          if (key === API_ERROR_LABEL) return apiErrorCount(s);
          return s.byEstadoContrato?.[key] ?? 0;
        }),
        borderColor: chartColor(key),
        backgroundColor: chartColor(key),
        tension: 0.2,
        fill: false,
      }));
      return { labels, datasets };
    },
    pieChartData() {
      const snap = this.selectedSnapshot;
      if (!snap) return null;
      const entries = Object.entries(snap.byEstadoContrato || {}).filter(
        ([k, v]) => v > 0 && k !== "Error_API" && k !== API_ERROR_LABEL,
      );
      const errTotal = apiErrorCount(snap);
      if (errTotal > 0) entries.push([API_ERROR_LABEL, errTotal]);
      if (!entries.length) return null;
      return {
        labels: entries.map(([k]) => k),
        datasets: [
          {
            data: entries.map(([, v]) => v),
            backgroundColor: entries.map(([k]) => chartColor(k)),
          },
        ],
      };
    },
  },
  mounted() {
    this.loadSnapshots();
  },
  methods: {
    formatDate(iso) {
      if (!iso) return "—";
      return moment(iso).format("DD/MM/YYYY HH:mm");
    },
    applyResponse(data) {
      this.snapshots = data.snapshots || [];
      this.estados = data.estados || [];
      this.latest = data.latest || null;
      this.selectedSnapshotId = data.latest?.id || null;
      const last =
        this.snapshots.find((s) => s.id === data.latest?.id) ||
        this.snapshots[this.snapshots.length - 1];
      this.lastSnapshotMeta = last || {};
    },
    async loadSnapshots() {
      this.loadLoading = true;
      this.error = "";
      try {
        const res = await this.$https.get("/leads/contratoEvolucion/snapshots", {
          params: { role: this.role, limit: 52 },
        });
        this.applyResponse(res.data);
      } catch (e) {
        this.error =
          e.response?.data?.message ||
          e.response?.data?.error ||
          "No se pudo cargar el historial";
      } finally {
        this.loadLoading = false;
      }
    },
    async runSnapshot() {
      const ok = window.confirm(
        "Se consultará la API de Saludsa para todos los leads con número de contrato. Puede tardar varios minutos. ¿Continuar?",
      );
      if (!ok) return;

      this.runLoading = true;
      this.error = "";
      try {
        await this.$https.post(
          "/leads/contratoEvolucion/run",
          {
            tokenAuth: this.tokenAuth,
            role: this.role,
          },
          { timeout: 300000 },
        );
        await this.loadSnapshots();
      } catch (e) {
        this.error =
          e.response?.data?.message ||
          e.response?.data?.error ||
          "Error al generar snapshot";
      } finally {
        this.runLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.debug-toggle {
  text-decoration: none !important;
}
.debug-toggle:hover {
  text-decoration: underline !important;
}
</style>
